import type { EventListItem } from "@cdt/shared";
import { zodResolver } from "@hookform/resolvers/zod";
import { Check, ChevronRight, Loader2, Save, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useEventQuery } from "@/hooks/useEventQuery";
import { useCategoriesQuery } from "@/hooks/useCategoriesQuery";
import {
  useApproveEvent,
  useRejectEvent,
  useUpdateEvent,
} from "@/hooks/useEventActions";
import { formatConfidence, formatDateTimeLong } from "@/lib/formatters";
import { RejectDialog } from "./RejectDialog";
import {
  ReviewForm,
  ReviewFormSchema,
  buildPatchFromForm,
  defaultsFromEvent,
  type ReviewFormValues,
} from "./ReviewForm";

interface ReviewCardProps {
  event: EventListItem;
}

export function ReviewCard({ event }: ReviewCardProps) {
  const defaults = useMemo<ReviewFormValues>(
    () =>
      defaultsFromEvent({
        ...event,
        emailMessageId: "",
        emailBody: null,
        calendarEventId: null,
        errorMessage: null,
        retryCount: 0,
        stage1Verdict: null,
        rawAiResponse: null,
        prioritySetBy: null,
        prioritySetAt: null,
        reviewedBy: null,
        reviewedAt: null,
      }),
    [event],
  );

  const form = useForm<ReviewFormValues>({
    resolver: zodResolver(ReviewFormSchema),
    defaultValues: defaults,
    mode: "onSubmit",
  });

  // Keep the form in sync when a refetch changes the underlying event values
  // (e.g. after an optimistic update rolls back on error).
  useEffect(() => {
    form.reset(defaults);
  }, [defaults, form]);

  const updateMutation = useUpdateEvent();
  const approveMutation = useApproveEvent();
  const rejectMutation = useRejectEvent();
  const categoriesQuery = useCategoriesQuery();

  const isPending =
    updateMutation.isPending ||
    approveMutation.isPending ||
    rejectMutation.isPending;

  const [rejectOpen, setRejectOpen] = useState(false);
  const [pendingApproveConfirm, setPendingApproveConfirm] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  const isDirty = form.formState.isDirty;

  const onSave = form.handleSubmit((values) => {
    const patch = buildPatchFromForm(values, form.formState.dirtyFields);
    if (Object.keys(patch).length === 0) return;
    updateMutation.mutate(
      { id: event.id, patch },
      {
        onSuccess: (detail) => {
          form.reset(defaultsFromEvent(detail));
        },
      },
    );
  });

  const approveNow = () => {
    approveMutation.mutate({ id: event.id });
    setPendingApproveConfirm(false);
  };

  const onApproveClick = () => {
    if (isDirty) {
      setPendingApproveConfirm(true);
      return;
    }
    approveNow();
  };

  const onSaveThenApprove = form.handleSubmit((values) => {
    const patch = buildPatchFromForm(values, form.formState.dirtyFields);
    if (Object.keys(patch).length === 0) {
      approveNow();
      return;
    }
    updateMutation.mutate(
      { id: event.id, patch },
      {
        onSuccess: () => {
          approveNow();
        },
      },
    );
  });

  const onRejectConfirm = (reason: string | undefined) => {
    rejectMutation.mutate(
      { id: event.id, reason },
      {
        onSuccess: () => setRejectOpen(false),
        onError: () => setRejectOpen(false),
      },
    );
  };

  // Ctrl/Cmd+Enter inside the card triggers save (if dirty) or approve.
  // Esc resets the form when dirty.
  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      if (isDirty) {
        void onSave();
      } else {
        onApproveClick();
      }
      return;
    }
    if (e.key === "Escape" && isDirty) {
      e.preventDefault();
      form.reset(defaults);
    }
  };

  return (
    <Card ref={cardRef} onKeyDown={onKeyDown}>
      <CardHeader className="border-b">
        <div className="flex flex-wrap items-start gap-x-4 gap-y-2">
          <div className="min-w-0 flex-1">
            <h3 className="truncate text-base font-semibold" title={event.emailSubject}>
              {event.emailSubject}
            </h3>
            <p className="mt-0.5 text-xs text-muted-foreground">
              da <span className="font-medium">{event.emailFrom}</span>
              {" - "}
              ricevuta {formatDateTimeLong(event.emailReceivedAt)}
            </p>
          </div>
          <div className="shrink-0 text-xs text-muted-foreground">
            Confidenza: {formatConfidence(event.confidence)}
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-5 pt-6">
        <form onSubmit={onSave}>
          <ReviewForm
            form={form}
            categories={categoriesQuery.data?.items ?? []}
            disabled={isPending}
          />
          {/* Hidden submit so Enter key in input still submits via onSave. */}
          <button type="submit" className="hidden" aria-hidden="true" tabIndex={-1} />
        </form>

        <EmailBodySection eventId={event.id} />

        {pendingApproveConfirm ? (
          <ApproveWithDirtyBanner
            isPending={updateMutation.isPending || approveMutation.isPending}
            onSaveThenApprove={() => void onSaveThenApprove()}
            onApproveAnyway={approveNow}
            onCancel={() => setPendingApproveConfirm(false)}
          />
        ) : null}

        <div className="flex flex-wrap items-center justify-between gap-3 border-t pt-4">
          <div className="text-xs text-muted-foreground">
            {isDirty ? "Modifiche non salvate" : "Nessuna modifica"}
            {isPending ? (
              <span className="ml-2 inline-flex items-center gap-1">
                <Loader2 className="h-3 w-3 animate-spin" />
                in corso...
              </span>
            ) : null}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onSave()}
              disabled={!isDirty || isPending}
            >
              <Save className="h-4 w-4" />
              Salva
            </Button>
            <Button
              type="button"
              variant="destructive"
              size="sm"
              onClick={() => setRejectOpen(true)}
              disabled={isPending}
            >
              <X className="h-4 w-4" />
              Rifiuta
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={onApproveClick}
              disabled={isPending}
            >
              <Check className="h-4 w-4" />
              Approva
            </Button>
          </div>
        </div>
      </CardContent>

      <RejectDialog
        open={rejectOpen}
        onOpenChange={(open) => {
          if (!rejectMutation.isPending) setRejectOpen(open);
        }}
        onConfirm={onRejectConfirm}
        isPending={rejectMutation.isPending}
        eventSubject={event.emailSubject}
      />
    </Card>
  );
}

interface ApproveBannerProps {
  isPending: boolean;
  onSaveThenApprove: () => void;
  onApproveAnyway: () => void;
  onCancel: () => void;
}

function ApproveWithDirtyBanner({
  isPending,
  onSaveThenApprove,
  onApproveAnyway,
  onCancel,
}: ApproveBannerProps) {
  return (
    <div className="rounded-md border border-amber-400/50 bg-amber-50 p-3 text-sm text-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
      <p className="font-medium">Hai modifiche non salvate</p>
      <p className="mt-0.5 text-xs">
        Vuoi salvarle prima di approvare o ignorarle?
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        <Button
          type="button"
          size="sm"
          onClick={onSaveThenApprove}
          disabled={isPending}
        >
          Salva e approva
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onApproveAnyway}
          disabled={isPending}
        >
          Approva senza salvare
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onCancel}
          disabled={isPending}
        >
          Annulla
        </Button>
      </div>
    </div>
  );
}

// Lazy-loads the full event detail only when the user expands the panel so
// the review queue query can stay list-shaped (no `emailBody` on the wire).
function EmailBodySection({ eventId }: { eventId: string }) {
  const [open, setOpen] = useState(false);
  const detail = useEventQuery(open ? eventId : undefined);

  return (
    <details
      className="rounded-md border bg-muted/30 text-sm"
      onToggle={(e) => setOpen((e.target as HTMLDetailsElement).open)}
    >
      <summary className="flex cursor-pointer list-none items-center gap-2 px-3 py-2 font-medium text-muted-foreground hover:text-foreground">
        <ChevronRight
          className={`h-4 w-4 transition-transform ${open ? "rotate-90" : ""}`}
        />
        Email originale
      </summary>
      <div className="border-t px-3 py-3">
        {!open ? null : detail.isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-3 w-full" />
            <Skeleton className="h-3 w-5/6" />
            <Skeleton className="h-3 w-4/6" />
          </div>
        ) : detail.isError ? (
          <p className="text-xs text-destructive">
            Errore nel caricamento del corpo email.
          </p>
        ) : detail.data?.emailBody ? (
          <pre className="max-h-[320px] overflow-auto whitespace-pre-wrap break-words font-mono text-xs">
            {detail.data.emailBody}
          </pre>
        ) : (
          <p className="text-xs text-muted-foreground">
            Corpo non disponibile.
          </p>
        )}
      </div>
    </details>
  );
}
